import React from "react";
import { Card, Row, Col, Image, Button, Form } from "react-bootstrap";
import "./VehicleList.css";
import { normalizeImages } from "../../../../utils/normalizeImages";

const VehicleList = ({ vehicles = [], onEdit, onDelete, onAvailableChange }) => {
  if (!vehicles || vehicles.length === 0) {
    return <p className="text-muted text-center mt-4">No vehicles found.</p>;
  }

  const vehicleMakeDetails = (vehicle) => {
    try {
      const details = JSON.parse(vehicle.vehicle_type_details || "{}");
      return details;
    } catch {
      return {};
    }
  };

  return (
    <div className="vehicle-list-container">
      {vehicles.map((vehicle, index) => (
        <Card key={index} className="vehicle-card mb-4 shadow-sm">
          <Card.Body>
            <div className="vehicle-card-header d-flex justify-content-between align-items-center mb-3 w-100 mt-2">
              <h5 className="vehicle-title mb-0">
                🚚 {vehicle.vehicle_reg_no || "Unnamed Vehicle"}
              </h5>
              <div className="vehicle-actions">
                {onEdit && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={() => onEdit(vehicle)}
                  >
                    <i className="bi bi-pencil"></i> Edit
                  </Button>
                )}
                {onDelete && (
                  <Button variant="outline-danger" size="sm" onClick={() => onDelete(vehicle.id)}>
                    <i className="bi bi-trash"></i> Delete
                  </Button>
                )}
              </div>
            </div>

            <Row>
              <Col md={6} className="text-start">
                <h5 className="vehicle-subtitle mb-3 text-decoration-underline">Driver Details</h5>
                <p>
                  <strong>Name:</strong> {vehicle.driver_name}
                </p>
                <p>
                  <strong>Contact:</strong> {vehicle.driver_contact}
                </p>
                <p>
                  <strong>Email:</strong> {vehicle.driver_email || "-"}
                </p>
                <p>
                  <strong>License No:</strong> {vehicle.driver_license_no}
                </p>
                {vehicle.driver_license_copy && (
                  <div className="preview-img">
                    <a href={vehicle.driver_license_copy} target="_blank" rel="noopener noreferrer">
                      <Image src={vehicle.driver_license_copy} thumbnail />
                    </a>
                  </div>
                )}
              </Col>

              <Col md={6} className="text-start">
                <h5 className="vehicle-subtitle mb-3 text-decoration-underline">Vehicle Details</h5>
                <p>
                  <strong>Owner:</strong> {vehicle.vehicle_owner_name}
                </p>
                <p>
                  <strong>Reg No:</strong> {vehicle.vehicle_reg_no}
                </p>
                <p>
                  <strong>RC No:</strong> {vehicle.vehicle_rc_no}
                </p>
                <p>
                  <strong>Make & Model:</strong>{" "}
                  {vehicleMakeDetails(vehicle)?.make + " - " + vehicleMakeDetails(vehicle)?.model ||
                    vehicle.vehicle_make}
                </p>
                <p>
                  <strong>Body Type:</strong> {vehicle.vehicle_body_type}
                </p>
                <p>
                  <strong>Payload:</strong>{" "}
                  {vehicleMakeDetails(vehicle)?.max_payload || vehicle.vehicle_payload_capacity}
                </p>
                <p>
                  <strong>Mileage:</strong> {vehicleMakeDetails(vehicle)?.mileage || "-"}
                </p>
                <p>
                  <strong>Fuel:</strong> {vehicleMakeDetails(vehicle)?.fuel || "-"}
                </p>
              </Col>
              <hr style={{ borderColor: "#bcbbbb" }} />
            </Row>
            <Row>
              <Col md={6} className="text-start">
                <p>
                  <div className="d-flex align-items-center">
                    <span className="me-2 fw-bold">
                      Available:{" "}
                      {vehicle.is_available ? (
                        <span className="text-success">✅ Yes</span>
                      ) : (
                        <span className="text-danger">❌ No</span>
                      )}
                    </span>
                    <Form.Check
                      className="large-switch"
                      type="switch"
                      id={`available-check-${vehicle.id}`}
                      checked={vehicle.is_available}
                      onChange={(e) => onAvailableChange(vehicle.id)}
                    />
                  </div>
                </p>
                <p>
                  <span className="me-2 fw-bold">
                    Vehicle Status:{" "}
                    {vehicle?.is_busy ? (
                      <span className="text-danger">❌ Busy. In Transit.</span>
                    ) : (
                      <span className="text-success">✅ Free</span>
                    )}
                  </span>
                </p>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6} className="text-start">
                <h6 className="vehicle-subtitle mb-3 text-decoration-underline">Vehicle Images</h6>
                <div className="vehicle-images-gallery">
                  {Array.isArray(vehicle.vehicle_images)
                    ? vehicle.vehicle_images.map((imgUrl, imgIndex) => (
                        <a key={imgIndex} href={imgUrl} target="_blank" rel="noopener noreferrer">
                          <Image src={imgUrl} thumbnail className="vehicle-img" />
                        </a>
                      ))
                    : (() => {
                        try {
                          const parsed = normalizeImages(vehicle.vehicle_images);
                          return parsed.map((imgUrl, imgIndex) => (
                            <a
                              key={imgIndex}
                              href={imgUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Image src={imgUrl} thumbnail className="vehicle-img" />
                            </a>
                          ));
                        } catch {
                          return <p className="text-muted">No images available</p>;
                        }
                      })()}
                </div>
              </Col>
              <Col md={2} className="text-start">
                <h6 className="vehicle-subtitle mb-3 text-decoration-underline">Insurance Copy</h6>
                <div className="vehicle-images-gallery">
                  {vehicle.insurance_copy && (
                    <a
                      key={"imgIndex-insurance"}
                      href={vehicle.insurance_copy}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image src={vehicle.insurance_copy} thumbnail className="vehicle-img" />
                    </a>
                  )}
                </div>
              </Col>
              <Col md={2} className="text-start">
                <h6 className="vehicle-subtitle mb-3 text-decoration-underline">RC Copy</h6>
                <div className="vehicle-images-gallery">
                  {vehicle.vehicle_rc_copy && (
                    <a
                      key={"imgIndex-rc-copy"}
                      href={vehicle.vehicle_rc_copy}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image src={vehicle.vehicle_rc_copy} thumbnail className="vehicle-img" />
                    </a>
                  )}
                </div>
              </Col>
              <Col md={2} className="text-start">
                <h6 className="vehicle-subtitle mb-3 text-decoration-underline">
                  Transport Permit
                </h6>
                <div className="vehicle-images-gallery">
                  {vehicle.transport_permit_copy && (
                    <a
                      key={"imgIndex-transport"}
                      href={vehicle.transport_permit_copy}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image
                        src={vehicle.transport_permit_copy}
                        thumbnail
                        className="vehicle-img"
                      />
                    </a>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
};

export default VehicleList;
